import React, { useEffect, useMemo, useState } from "react";
import {
  Button, Col, DatePicker, Descriptions, Divider, Empty, Form,
  Input, InputNumber, Modal, Popconfirm, Row, Select, Space, Spin,
  Table, Tag, Typography, message,
} from "antd";
import {
  DeleteOutlined, EditOutlined, PlusOutlined, PrinterOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { adminLaundryService } from "../../services/adminService";
import adminServices from "../../services/adminService";
import { formatLkr } from "../../utils/currency";
import { printJobService } from "../../services/printerService";

const { Text, Title } = Typography;
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1";
const getToken = () => localStorage.getItem("admin_token") || "";

const CLOTHING_TYPES = [
  "T-Shirt","Shirt","Polo Shirt","Blouse","Dress Shirt","Tank Top",
  "Trousers","Jeans","Shorts","Skirt","Dress","Suit Jacket","Blazer",
  "Suit Trousers","Sweater","Hoodie","Jacket","Coat","Saree","Kurta",
  "Pyjamas / Sleepwear","Underwear","Socks","Bed Sheets","Pillow Cases",
  "Towel","Bath Robe","Table Cloth","Curtain","Other",
];

const STATUS_COLORS: Record<string, string> = {
  pending: "default", collected: "processing", washing: "blue",
  ready: "cyan", delivered: "geekblue", billed: "purple",
  paid: "green", cancelled: "red",
};

const STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "Collected", value: "collected" },
  { label: "Washing", value: "washing" },
  { label: "Ready", value: "ready" },
  { label: "Delivered", value: "delivered" },
  { label: "Billed", value: "billed" },
  { label: "Paid", value: "paid" },
  { label: "Cancelled", value: "cancelled" },
];

interface LaundryItem {
  clothing_type: string;
  quantity: number;
  weight_kg?: number | null;
  unit_price: number;
  line_total: number;
  note?: string;
}

interface LaundryOrder {
  id: string;
  order_number: string;
  booking_id?: string;
  room_id?: string;
  room_number?: string;
  guest_name?: string;
  guest_phone?: string;
  guest_email?: string;
  status: string;
  notes?: string;
  total_amount: number;
  service_type: string;
  service_date: string;
  created_at: string;
  items: LaundryItem[];
}

// ---------------------------------------------------------------------------
// Create / Edit Modal
// ---------------------------------------------------------------------------
const CreateLaundryOrderModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  onSaved: () => void;
  editingOrder?: LaundryOrder | null;
}> = ({ visible, onClose, onSaved, editingOrder }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [checkedInBookings, setCheckedInBookings] = useState<any[]>([]);
  const [items, setItems] = useState<LaundryItem[]>([
    { clothing_type: "", quantity: 1, weight_kg: null, unit_price: 0, line_total: 0 },
  ]);

  useEffect(() => {
    adminServices.frontDeskService.getBookings({ status: "checked_in" }).then((res) => {
      setCheckedInBookings(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (visible && editingOrder) {
      form.setFieldsValue({
        booking_id: editingOrder.booking_id,
        room_number: editingOrder.room_number,
        guest_name: editingOrder.guest_name,
        guest_phone: editingOrder.guest_phone,
        guest_email: editingOrder.guest_email,
        notes: editingOrder.notes,
        service_type: editingOrder.service_type || 'laundry',
        service_date: editingOrder.service_date ? dayjs(editingOrder.service_date) : dayjs(),
      });
      setItems(editingOrder.items.map((it) => ({ ...it, line_total: Number(it.line_total ?? it.unit_price) })));
    } else if (visible && !editingOrder) {
      form.resetFields();
      setItems([{ clothing_type: "", quantity: 1, weight_kg: null, unit_price: 0, line_total: 0 }]);
      form.setFieldsValue({ service_type: 'laundry', service_date: dayjs() });
    }
  }, [visible, editingOrder, form]);

  const handleBookingSelect = (bookingId: string) => {
    const b = checkedInBookings.find((x) => x.id === bookingId);
    if (b) {
      form.setFieldsValue({
        room_number: b.room_number || b.room_id || "",
        guest_name: b.guest_name || "",
        guest_phone: b.phone || "",
        guest_email: b.email || "",
      });
    }
  };

  const updateItem = (idx: number, field: keyof LaundryItem, value: any) => {
    setItems((prev) => {
      const next = [...prev];
      (next[idx] as any)[field] = value;
      // line_total is now entered directly — no calculation needed
      return next;
    });
  };

  const addItem = () => setItems((p) => [...p, { clothing_type: "", quantity: 1, weight_kg: null, unit_price: 0, line_total: 0 }]);
  const removeItem = (idx: number) => setItems((p) => p.filter((_, i) => i !== idx));

  const grandTotal = items.reduce((s, it) => s + (it.line_total || 0), 0);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (items.some((it) => !it.clothing_type)) {
        message.error("Please select a clothing type for all items");
        return;
      }
      setLoading(true);
      const payload = {
        ...values,
        service_type: values.service_type || 'laundry',
        service_date: values.service_date ? values.service_date.format('YYYY-MM-DD') : undefined,
        items: items.map((it) => ({
          clothing_type: it.clothing_type,
          quantity: Number(it.quantity),
          weight_kg: it.weight_kg || null,
          unit_price: Number(it.line_total),
          note: it.note || null,
        })),
      };
      if (editingOrder) {
        await adminLaundryService.updateOrder(editingOrder.id, payload);
        message.success("Laundry order updated");
      } else {
        await adminLaundryService.createOrder(payload);
        message.success("Laundry order created");
      }
      onSaved();
      onClose();
    } catch (err: any) {
      if (err?.errorFields) return; // validation errors
      message.error("Failed to save laundry order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      title={editingOrder ? "Edit Laundry Order" : "New Laundry Order"}
      onCancel={onClose}
      width={860}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="save" type="primary" loading={loading} onClick={handleSubmit}
          style={{ backgroundColor: "#b79a68", borderColor: "#b79a68" }}>
          {editingOrder ? "Save Changes" : "Create Order"}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Select Booking (Checked-In Room)" name="booking_id">
              <Select
                placeholder="Select checked-in room"
                allowClear
                showSearch
                filterOption={(input, opt) => String(opt?.label || "").toLowerCase().includes(input.toLowerCase())}
                onChange={handleBookingSelect}
                options={checkedInBookings.map((b) => ({
                  label: `Room ${b.room_number || "?"} — ${b.guest_name} (${b.confirmation_number || b.id.slice(0,8)})`,
                  value: b.id,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Room Number" name="room_number">
              <Input placeholder="e.g. 101" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Guest Name" name="guest_name">
              <Input placeholder="Guest name" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Guest Phone" name="guest_phone">
              <Input placeholder="Phone number" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Guest Email" name="guest_email">
              <Input placeholder="Email" />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item label="Service Type" name="service_type" rules={[{ required: true }]}>
              <Select
                options={[
                  { label: "Laundry", value: "laundry" },
                  { label: "Ironing", value: "ironing" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item label="Service Date" name="service_date" rules={[{ required: true }]}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ color: "#b79a68", borderColor: "#b79a68" }}>Clothing Items</Divider>

        {/* Table header */}
        <Row gutter={8} style={{ marginBottom: 4, fontWeight: 600, fontSize: 12, color: "#888" }}>
          <Col span={8}>Clothing Type</Col>
          <Col span={3}>Qty</Col>
          <Col span={5}>Weight (kg)</Col>
          <Col span={6}>Total Price (LKR)</Col>
          <Col span={2}></Col>
        </Row>
        {items.map((item, idx) => (
          <Row gutter={8} key={idx} style={{ marginBottom: 8 }} align="middle">
            <Col span={8}>
              <Select
                value={item.clothing_type || undefined}
                placeholder="Select type"
                style={{ width: "100%" }}
                showSearch
                onChange={(v) => updateItem(idx, "clothing_type", v)}
                options={CLOTHING_TYPES.map((t) => ({ label: t, value: t }))}
              />
            </Col>
            <Col span={3}>
              <InputNumber min={1} value={item.quantity} style={{ width: "100%" }}
                onChange={(v) => updateItem(idx, "quantity", v || 1)} />
            </Col>
            <Col span={5}>
              <InputNumber min={0} step={0.01} value={item.weight_kg ?? undefined}
                placeholder="Optional" style={{ width: "100%" }}
                onChange={(v) => updateItem(idx, "weight_kg", v)} />
            </Col>
            <Col span={6}>
              <InputNumber
                min={0}
                step={50}
                value={item.line_total}
                style={{ width: "100%" }}
                placeholder="Enter total price"
                onChange={(v) => updateItem(idx, "line_total", v || 0)}
              />
            </Col>
            <Col span={2}>
              <Button danger size="small" icon={<DeleteOutlined />}
                onClick={() => removeItem(idx)} disabled={items.length === 1} />
            </Col>
          </Row>
        ))}

        <Button type="dashed" onClick={addItem} icon={<PlusOutlined />} style={{ width: "100%", marginBottom: 16 }}>
          Add Item
        </Button>

        <Row justify="end">
          <Col>
            <Text strong style={{ fontSize: 16, color: "#b79a68" }}>
              Grand Total: {formatLkr(grandTotal)}
            </Text>
          </Col>
        </Row>

        <Form.Item label="Notes" name="notes" style={{ marginTop: 16 }}>
          <Input.TextArea rows={2} placeholder="Any special instructions..." />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Order Details Modal
// ---------------------------------------------------------------------------
const OrderDetailsModal: React.FC<{
  order: LaundryOrder | null;
  onClose: () => void;
  onStatusChange: (id: string, status: string) => void;
  onEdit: (order: LaundryOrder) => void;
}> = ({ order, onClose, onStatusChange, onEdit }) => {
  if (!order) return null;

  const printReceipt = async () => {
    try {
      await printJobService.submit({
        idempotency_key: `laundry-${order.id}-${Date.now()}`,
        printer_role: 'LAUNDRY',
        content_type: 'laundry',
        order_id: order.id,
      });
      message.success('Receipt sent to laundry printer');
    } catch {
      message.error("Failed to send receipt to printer");
    }
  };

  return (
    <Modal open={!!order} title={`Laundry Order — ${order.order_number}`}
      onCancel={onClose} width={700}
      footer={[
        <Button key="edit" icon={<EditOutlined />} onClick={() => onEdit(order)}>Edit</Button>,
        <Button key="print" type="default" icon={<PrinterOutlined />} onClick={printReceipt}>Print Receipt</Button>,
        <Button key="close" type="primary" onClick={onClose}
          style={{ backgroundColor: "#b79a68", borderColor: "#b79a68" }}>Close</Button>,
      ]}
    >
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Order No">{order.order_number}</Descriptions.Item>
        <Descriptions.Item label="Date">{new Date(order.created_at).toLocaleString()}</Descriptions.Item>
        <Descriptions.Item label="Room">{order.room_number || "—"}</Descriptions.Item>
        <Descriptions.Item label="Guest">{order.guest_name || "—"}</Descriptions.Item>
        <Descriptions.Item label="Phone">{order.guest_phone || "—"}</Descriptions.Item>
        <Descriptions.Item label="Email">{order.guest_email || "—"}</Descriptions.Item>
        <Descriptions.Item label="Status" span={2}>
          <Select value={order.status} style={{ width: 160 }}
            options={STATUS_OPTIONS}
            onChange={(s) => onStatusChange(order.id, s)} />
        </Descriptions.Item>
        {order.notes && <Descriptions.Item label="Notes" span={2}>{order.notes}</Descriptions.Item>}
      </Descriptions>

      <Divider>Items</Divider>
      <Table
        dataSource={order.items}
        pagination={false}
        size="small"
        rowKey={(_, i) => String(i)}
        columns={[
          { title: "Clothing Type", dataIndex: "clothing_type", key: "type" },
          { title: "Qty", dataIndex: "quantity", key: "qty", width: 60 },
          { title: "Weight (kg)", dataIndex: "weight_kg", key: "wt", width: 100,
            render: (v) => v != null ? Number(v).toFixed(2) : "—" },
          { title: "Total Price", dataIndex: "line_total", key: "total", width: 120,
            render: (v) => <Text strong style={{ color: "#b79a68" }}>{formatLkr(Number(v))}</Text> },
        ]}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={3}>
              <Text strong>Grand Total</Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3}>
              <Text strong style={{ color: "#b79a68", fontSize: 15 }}>
                {formatLkr(Number(order.total_amount))}
              </Text>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
const LaundryService: React.FC = () => {
  const [orders, setOrders] = useState<LaundryOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [createVisible, setCreateVisible] = useState(false);
  const [detailOrder, setDetailOrder] = useState<LaundryOrder | null>(null);
  const [editingOrder, setEditingOrder] = useState<LaundryOrder | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterServiceType, setFilterServiceType] = useState<string>("all");
  const [filterDate, setFilterDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(h);
  }, [search]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const hasSearch = debouncedSearch.length > 0;
      const res = await adminLaundryService.getOrders({
        status: filterStatus !== "all" ? filterStatus : undefined,
        service_type: filterServiceType !== "all" ? filterServiceType : undefined,
        search: debouncedSearch || undefined,
        startDate: (!hasSearch && filterDate) ? filterDate.startOf("day").toISOString() : undefined,
        endDate: (!hasSearch && filterDate) ? filterDate.endOf("day").toISOString() : undefined,
      });
      setOrders(Array.isArray(res.data) ? (res.data as LaundryOrder[]) : []);
    } catch {
      message.error("Failed to load laundry orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, [filterStatus, filterServiceType, filterDate, debouncedSearch]); // eslint-disable-line

  const updateStatus = async (id: string, status: string) => {
    try {
      await adminLaundryService.updateOrderStatus(id, status);
      message.success("Status updated");
      fetchOrders();
      if (detailOrder?.id === id) {
        setDetailOrder((prev) => prev ? { ...prev, status } : null);
      }
    } catch {
      message.error("Failed to update status");
    }
  };

  const deleteOrder = async (id: string) => {
    try {
      await adminLaundryService.deleteOrder(id);
      message.success("Order deleted");
      fetchOrders();
    } catch {
      message.error("Failed to delete order");
    }
  };

  const printReceipt = async (id: string) => {
    try {
      await printJobService.submit({
        idempotency_key: `laundry-${id}-${Date.now()}`,
        printer_role: 'LAUNDRY',
        content_type: 'laundry',
        order_id: id,
      });
      message.success('Receipt sent to laundry printer');
    } catch {
      message.error("Failed to send receipt to printer");
    }
  };


  const columns = [
    {
      title: "Order",
      key: "order",
      render: (_: any, r: LaundryOrder) => (
        <div style={{ cursor: "pointer" }} onClick={() => setDetailOrder(r)}>
          <Text strong style={{ color: "#b79a68" }}>{r.order_number}</Text>
          <br />
          <Tag color={r.service_type === 'ironing' ? 'geekblue' : 'magenta'} style={{ fontSize: 10 }}>
            {r.service_type === 'ironing' ? 'Ironing' : 'Laundry'}
          </Tag>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {r.service_date ? new Date(r.service_date).toLocaleDateString() : new Date(r.created_at).toLocaleDateString()}
          </Text>
        </div>
      ),
    },
    {
      title: "Room / Guest",
      key: "guest",
      render: (_: any, r: LaundryOrder) => (
        <div>
          <Text>{r.guest_name || "—"}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>Room: {r.room_number || "—"}</Text>
        </div>
      ),
    },
    {
      title: "Items",
      key: "items",
      render: (_: any, r: LaundryOrder) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {(r.items || []).length} item(s): {(r.items || []).slice(0, 2).map((i) => i.clothing_type).join(", ")}
          {(r.items || []).length > 2 ? ` +${(r.items || []).length - 2} more` : ""}
        </Text>
      ),
    },
    {
      title: "Total",
      dataIndex: "total_amount",
      key: "total",
      render: (v: number) => <Text strong style={{ color: "#b79a68" }}>{formatLkr(Number(v))}</Text>,
    },
    {
      title: "Status",
      key: "status",
      render: (_: any, r: LaundryOrder) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Select value={r.status} style={{ width: 130 }} size="small"
            options={STATUS_OPTIONS}
            onChange={(s) => updateStatus(r.id, s)} />
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, r: LaundryOrder) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Button size="small" icon={<PrinterOutlined />} onClick={() => printReceipt(r.id)} title="Print Receipt" />
          <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingOrder(r); setCreateVisible(true); }} />
          <Popconfirm title="Delete this laundry order?" onConfirm={() => deleteOrder(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="front-desk-container">
      <div className="front-desk-header">
        <h1>Laundry Service</h1>
        <p>Manage guest laundry orders, track item processing statuses, and generate receipts.</p>
      </div>

      <div className="front-desk-toolbar">
        <Space wrap>
          <DatePicker value={filterDate} onChange={setFilterDate} allowClear
            placeholder="Filter by date" style={{ width: 160 }} />
          <Select value={filterStatus} style={{ width: 140 }} onChange={setFilterStatus}
            options={[{ label: "All Statuses", value: "all" }, ...STATUS_OPTIONS]} />
          <Select value={filterServiceType} style={{ width: 140 }} onChange={setFilterServiceType}
            options={[
              { label: "All Types", value: "all" },
              { label: "Laundry", value: "laundry" },
              { label: "Ironing", value: "ironing" },
            ]} />
          <Input.Search placeholder="Search order, guest, room..." value={search}
            onChange={(e) => setSearch(e.target.value)} style={{ width: 260 }} allowClear />
        </Space>
        <Button type="primary" onClick={() => { setEditingOrder(null); setCreateVisible(true); }}
          style={{ backgroundColor: "#b79a68", borderColor: "#b79a68" }}>
          New Laundry Order
        </Button>
      </div>

      <Spin spinning={loading}>
        {orders.length === 0 && !loading ? (
          <Empty description="No laundry orders found" />
        ) : (
          <Table
            dataSource={orders}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 20 }}
            onRow={(r) => ({ onClick: () => setDetailOrder(r), style: { cursor: 'pointer' } })}
            size="middle"
          />
        )}
      </Spin>


      <CreateLaundryOrderModal
        visible={createVisible}
        onClose={() => { setCreateVisible(false); setEditingOrder(null); }}
        onSaved={fetchOrders}
        editingOrder={editingOrder}
      />

      <OrderDetailsModal
        order={detailOrder}
        onClose={() => setDetailOrder(null)}
        onStatusChange={updateStatus}
        onEdit={(o) => { setDetailOrder(null); setEditingOrder(o); setCreateVisible(true); }}
      />
    </div>
  );
};

export default LaundryService;
